import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, type Participant } from 'livekit-client';

type MicStatus = 'idle' | 'starting' | 'live' | 'denied' | 'error';
export type NarrationEvent = 'narration_start' | 'narration_end' | 'narration_stopped';
/** The agent worker's lifecycle, from the `lk.agent.state` participant attribute. */
export type AgentState = 'initializing' | 'idle' | 'listening' | 'thinking' | 'speaking' | null;

const AGENT_STATE_ATTRIBUTE = 'lk.agent.state';
const AGENT_STATES = ['initializing', 'idle', 'listening', 'thinking', 'speaking'];

interface Options {
  enabled: boolean;
  sessionId: string;
  shareToken: string;
  isMuted: boolean;
  onNarrationEvent: (event: NarrationEvent) => void;
  onFailure: () => void;
}

// Browser noise suppression on as well as the server's: with the server alone,
// room noise and stray voices still reached the transcriber and stopped the
// narration on production.
const captureOptions = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: false,
  channelCount: 1
};

// A cold staging worker has taken about 35 seconds to join. Let the backend's
// 50-second ready window finish before returning to socket voice.
const WORKER_OPENING_TIMEOUT_MS = 55000;

export function useLiveKitPresenter({
  enabled, sessionId, shareToken, isMuted, onNarrationEvent, onFailure
}: Options) {
  const [status, setStatus] = useState<MicStatus>('idle');
  const [micError, setMicError] = useState<string | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [agentState, setAgentState] = useState<AgentState>(null);
  const roomRef = useRef<Room | null>(null);
  const audioElementsRef = useRef<Set<HTMLMediaElement>>(new Set());
  const intentionalStopRef = useRef(false);
  const mutedRef = useRef(isMuted);
  const eventRef = useRef(onNarrationEvent);
  const failureRef = useRef(onFailure);
  mutedRef.current = isMuted;
  eventRef.current = onNarrationEvent;
  failureRef.current = onFailure;

  const stop = useCallback(() => {
    intentionalStopRef.current = true;
    const room = roomRef.current;
    roomRef.current = null;
    audioElementsRef.current.forEach((element) => element.remove());
    audioElementsRef.current.clear();
    if (room) void room.disconnect();
    setMediaStream(null);
    setStatus('idle');
  }, []);

  const retryMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    setStatus('starting');
    setMicError(null);
    try {
      const publication = await room.localParticipant.setMicrophoneEnabled(
        !mutedRef.current, captureOptions
      );
      const track = publication?.track?.mediaStreamTrack;
      setMediaStream(track ? new MediaStream([track]) : null);
      setStatus('live');
    } catch (error) {
      const name = (error as { name?: string })?.name;
      const denied = ['NotAllowedError', 'NotFoundError', 'NotReadableError', 'SecurityError'].includes(name || '');
      setStatus(denied ? 'denied' : 'error');
      setMicError(denied
        ? 'Microphone access is blocked — allow it in your browser to talk to the presenter.'
        : 'The microphone could not be started.');
    }
  }, []);

  const startAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.startAudio();
      setAudioBlocked(!room.canPlaybackAudio);
    } catch {
      setAudioBlocked(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !sessionId || !shareToken) return undefined;
    let active = true;
    let heardNarration = false;
    // True from the moment someone starts talking until the worker says what it
    // was. While set the agent plays a little quieter (DUCK_VOLUME), not muted: a TV or a
    // colleague in the room muted the presenter for as long as they talked (37 s
    // on production) while the deck kept advancing unheard.
    let interrupted = false;
    // 60%: someone talking in the room should barely be noticed; a real
    // question stops the narration within a couple of seconds anyway.
    const DUCK_VOLUME = 0.6;
    // Set once the agent has left `speaking` after an interruption. A fresh
    // `speaking` only counts as the new answer after this, so the narration the
    // worker has not finished cancelling cannot unmute us early.
    let leftSpeaking = false;
    let lastState: AgentState = null;
    // What the customer's own turn is doing, from the worker's `user_speech_*`
    // messages. While they are talking, or a question the worker accepted is
    // still being answered, nothing may lift the mute: `narration_start`/`end`
    // and the agent's own `speaking` state otherwise un-mute the tail of the
    // old narration, or the next segment, over the customer's voice.
    let userSpeakingSince = 0;
    let answerPendingSince = 0;
    let sawStopped = false;
    const USER_SPEAKING_MAX_MS = 20000;
    const ANSWER_PENDING_MAX_MS = 20000;
    // The worker can resume its narration after a false interruption without ever
    // sending `unduck` (it sent `user_speech_validation: unverified` instead), so
    // nothing else would lift a mute that was held while the customer spoke.
    // Release it ourselves once the customer has been quiet for a moment and the
    // worker has not told us the speech was a question for the agent.
    let releaseTimer: ReturnType<typeof setTimeout> | null = null;
    const RELEASE_GRACE_MS = 2000;
    const clearRelease = () => {
      if (releaseTimer) clearTimeout(releaseTimer);
      releaseTimer = null;
    };
    const releaseIfAgentSpeaking = () => {
      if (interrupted && lastState === 'speaking' && leftSpeaking) resume();
    };
    const holdMute = () => {
      const now = Date.now();
      if (userSpeakingSince && now - userSpeakingSince < USER_SPEAKING_MAX_MS) return true;
      return !!answerPendingSince && !sawStopped && now - answerPendingSince < ANSWER_PENDING_MAX_MS;
    };
    let openingTimer: ReturnType<typeof setTimeout> | null = null;
    const room = new Room({ adaptiveStream: false });
    intentionalStopRef.current = false;
    roomRef.current = room;
    setStatus('starting');
    setMicError(null);

    const applyMute = () => {
      audioElementsRef.current.forEach((element) => {
        element.muted = false;
        element.volume = interrupted ? DUCK_VOLUME : 1;
      });
    };
    // The worker judged the speech: not for the presenter. Full volume now, even
    // if the TV is still talking; nothing else lifted the duck while it was.
    const release = () => {
      clearRelease();
      userSpeakingSince = 0;
      answerPendingSince = 0;
      interrupted = false;
      applyMute();
    };
    const interrupt = () => {
      if (interrupted) return;
      interrupted = true;
      leftSpeaking = false;
      applyMute();
    };
    const resume = () => {
      if (!interrupted || holdMute()) return;
      interrupted = false;
      applyMute();
    };
    // The worker's own turn-taking decides this, not the browser: its VAD and
    // endpointing run on the (noise-cancelled) caller audio, and the state leaves
    // `speaking` only once it has actually cut the agent off. Listening to the
    // room's raw speaker levels instead would fire on every cough.
    const readAgentState = (participant: Participant) => {
      const raw = participant.attributes?.[AGENT_STATE_ATTRIBUTE];
      if (!raw || !AGENT_STATES.includes(raw)) return;
      const next = raw as AgentState;
      if (next === lastState) return;
      const previous = lastState;
      lastState = next;
      setAgentState(next);
      if (previous === 'speaking' && next !== 'speaking') {
        // Mute straight away. A normal end of narration also passes through here;
        // `narration_end` follows and lifts the mute again. No `narration_stopped`
        // from here: this state change arrives just before every normal
        // `narration_end`, and reporting it as a stop cancelled the auto-advance,
        // so the deck halted after its first segment. The worker sends
        // `narration_stopped` itself when it really cuts a narration off.
        //
        // Only while the customer is talking. An answer is spoken sentence by
        // sentence and the agent passes through `listening` between them;
        // muting on every one clipped the answer for 0.1-0.3 s at each
        // sentence boundary. A real barge-in is already muted by the worker's
        // `user_speech_state` and `duck`.
        if (userSpeakingSince || answerPendingSince) interrupt();
      }
      if (interrupted && next !== 'speaking') leftSpeaking = true;
      if (next === 'speaking' && interrupted && leftSpeaking) resume();
    };

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio) return;
      const element = track.attach();
      element.autoplay = true;
      element.volume = interrupted ? DUCK_VOLUME : 1;
      element.style.display = 'none';
      document.body.appendChild(element);
      audioElementsRef.current.add(element);
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((element) => {
        element.remove();
        audioElementsRef.current.delete(element);
      });
    });
    room.on(RoomEvent.ParticipantAttributesChanged, (_changed, participant) => {
      if (active && participant !== room.localParticipant) readAgentState(participant);
    });
    // The agent may already be in the room, and attribute events only fire on change.
    room.on(RoomEvent.ParticipantConnected, (participant) => {
      if (active) readAgentState(participant);
    });
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (active) setAudioBlocked(!room.canPlaybackAudio);
    });
    room.on(RoomEvent.DataReceived, (bytes, _participant, _kind, topic) => {
      if (!active || topic !== 'nuway.state') return;
      try {
        const data = JSON.parse(new TextDecoder().decode(bytes));
        if (['user_speech_state', 'user_speech_validation'].includes(data.type)) {
          // Diagnostics: VAD can detect noise or another person's speech, and an
          // addressed transcript does not verify who is speaking.
          console.info('[Presenter speech]', { ...data, received_at_ms: Date.now() });
        }
        // The worker's voice-activity detection heard someone start or stop
        // talking. Lower the agent until the worker says whether it was meant
        // for the presenter.
        if (data.type === 'user_speech_state') {
          userSpeakingSince = data.state === 'speaking' ? Date.now() : 0;
          clearRelease();
          if (data.state === 'speaking') interrupt();
          else releaseTimer = setTimeout(releaseIfAgentSpeaking, RELEASE_GRACE_MS);
        }
        if (data.type === 'user_speech_validation') {
          clearRelease();
          if (data.status === 'accepted') {
            // A real question for the agent: whatever plays before the worker
            // reports `narration_stopped` is the old narration, not the answer.
            answerPendingSince = Date.now();
            sawStopped = false;
            interrupt();
          } else {
            // Background speech (rejected) or not confirmed: carry on, loud.
            release();
          }
        }
        if (data.type === 'narration_stopped' && answerPendingSince) sawStopped = true;
        // The worker has the question and is preparing the answer; the
        // narration it interrupted is already stopped. Unmute now: the typing
        // sound and the hold phrases ("Bear with me…") play while the answer is
        // written, and staying muted until the answer began silenced them.
        if (data.type === 'thinking') {
          clearRelease();
          userSpeakingSince = 0;
          answerPendingSince = 0;
          sawStopped = true;
          interrupted = false;
          applyMute();
        }
        if (data.type === 'duck') interrupt();
        // The worker decided it was not an interruption after all and carried on.
        if (data.type === 'unduck') release();
        if (data.type === 'narration_start') {
          heardNarration = true;
          if (openingTimer) clearTimeout(openingTimer);
          if (answerPendingSince && sawStopped) answerPendingSince = 0;
          resume();
        }
        // A normal end: the mute the state change applied was only a precaution.
        if (data.type === 'narration_end') resume();
        if (['narration_start', 'narration_end', 'narration_stopped'].includes(data.type)) {
          eventRef.current(data.type as NarrationEvent);
        }
      } catch {
        // A different data message must not interrupt the presentation.
      }
    });
    room.on(RoomEvent.Disconnected, () => {
      if (active && !intentionalStopRef.current) failureRef.current();
    });

    async function connect() {
      try {
        const url = new URL(`${process.env.REACT_APP_API_BASE_URL || 'https://ai.staging.nuway.co'}/api/v1/sales/pro/livekit/token`);
        url.searchParams.set('session_id', sessionId);
        url.searchParams.set('token', shareToken);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`LiveKit token: ${response.status}`);
        const credentials: { url: string; token: string } = await response.json();
        if (!active || intentionalStopRef.current) return;
        // Relay only (LiveKit's TURN over 443). Where the direct UDP route is
        // blocked or slow, the default tries it first and falls back only after
        // timing out: joining took 9-14 s from one network and 34-56 s in the
        // browser, the page's 55 s watchdog fired, and the presentation dropped
        // to socket audio (voice=1). Relay-only joined in about 2.3 s. It is
        // audio only, so the relay costs little.
        await room.connect(credentials.url, credentials.token, {
          autoSubscribe: true,
          rtcConfig: { iceTransportPolicy: 'relay' },
        });
        if (!active || intentionalStopRef.current) return;
        setAudioBlocked(!room.canPlaybackAudio);
        await retryMic();
        if (!active || intentionalStopRef.current) return;
        // If no worker ever accepts the presentation, return to the working
        // socket voice path instead of leaving the deck silent indefinitely.
        openingTimer = setTimeout(() => {
          if (active && !intentionalStopRef.current && !heardNarration) failureRef.current();
        }, WORKER_OPENING_TIMEOUT_MS);
      } catch {
        if (active && !intentionalStopRef.current) failureRef.current();
      }
    }
    void connect();
    return () => {
      active = false;
      intentionalStopRef.current = true;
      if (openingTimer) clearTimeout(openingTimer);
      clearRelease();
      roomRef.current = null;
      audioElementsRef.current.forEach((element) => element.remove());
      audioElementsRef.current.clear();
      void room.disconnect();
      setMediaStream(null);
      setAgentState(null);
    };
  }, [enabled, sessionId, shareToken, retryMic]);

  useEffect(() => {
    const room = roomRef.current;
    if (!room || status !== 'live') return;
    void room.localParticipant.setMicrophoneEnabled(!isMuted)
      .then((publication) => {
        const track = publication?.track?.mediaStreamTrack;
        if (track) setMediaStream(new MediaStream([track]));
      })
      .catch(() => setMicError('The microphone could not be updated.'));
  }, [isMuted, status]);

  return { status, micError, mediaStream, audioBlocked, agentState, retryMic, startAudio, stop };
}
